import React, { useState } from 'react';
import Papa from 'papaparse';

interface UploadStepProps {
  onParsed: (rows: any[]) => void;
  onModeChange?: (containsDates: boolean) => void;
}

export default function UploadStep({ onParsed, onModeChange }: UploadStepProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [containsDates, setContainsDates] = useState<boolean>(true);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Enforce ≤ 5 MB before parse
    const maxBytes = 5 * 1024 * 1024;
    if (file.size > maxBytes) {
      setError('File too large. Please upload a CSV ≤ 5 MB.');
      return;
    }

    setIsUploading(true);
    setError(null);

    // notify parent of mode
    try { onModeChange && onModeChange(containsDates); } catch {}

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setIsUploading(false);
        if (results.errors.length > 0) {
          setError('Error parsing CSV file. Please check the format.');
          return;
        }
        onParsed(results.data);
      },
      error: () => {
        setIsUploading(false);
        setError('Failed to read CSV file. Please try again.');
      }
    });
  };

  return (
    <div className="max-w-2xl mx-auto p-6 rounded-2xl border border-gray-200 bg-white shadow-sm dark:bg-neutral-900 dark:border-neutral-800">
      <div className="mb-4 flex items-center gap-2">
        <input
          id="contains-dates"
          type="checkbox"
          checked={containsDates}
          onChange={(e)=>{ setContainsDates(e.target.checked); try { onModeChange && onModeChange(e.target.checked); } catch {} }}
        />
        <label htmlFor="contains-dates" className="text-sm text-gray-800">
          CSV contains dates
        </label>
      </div>
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
        <input
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          disabled={isUploading}
          className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-sedra/10 file:text-sedra hover:file:bg-sedra/20 disabled:opacity-50"
        />
        {isUploading && (
          <p className="mt-4 text-sedra">Processing CSV file...</p>
        )}
        {error && (
          <p className="mt-4 text-red-600">{error}</p>
        )}
        {!isUploading && !error && (
          <p className="mt-2 text-gray-600">Upload your CSV file with service data (CSV, ≤ 5 MB)</p>
        )}
        <div className="mt-6">
          <a
            href={URL.createObjectURL(new Blob([
              containsDates
                ? 'Customer,Service Date,Service Type,Quantity,Rate\nAcme Co,2025-08-01,Grass Cut,1,80\nAcme Co,2025-08-08,Grass Cut,1,80\nBeta LLC,2025-08-03,Leaf Blowing,2,45\n'
                : 'Customer,Service 1,Service 2,Service 3\n11 Wayne Ave,GRASS CUT,GRASS CUT,\n14 Lath Ln,GRASS CUT,,\n170 Convent Rd,GRASS CUT,GRASS CUT,GRASS CUT\n'
            ], { type: 'text/csv' }))}
            download={containsDates ? 'batch-invoice-sample.csv' : 'batch-invoice-no-dates-sample.csv'}
            className="inline-block text-sedra hover:text-sedra-700 underline"
          >
            Download sample CSV
          </a>
        </div>
      </div>
    </div>
  );
} 